import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadCrumbsComponent } from 'src/app/Shared/components/bread-crumbs/bread-crumbs.component';
import { Breadcrumb } from 'src/app/Shared/_models/breadcrumb.model';

import { TimelineViewComponent } from './components/timeline-view/timeline-view.component';
import { AuctionEvent, getTranslation } from './models/auction-event.model';
import { AuctionsCalendarApiService } from './services/auctions-calendar-api.service';
import { TranslateModule, TranslateService as NgxTranslateService } from '@ngx-translate/core';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

@Component({
    selector: 'app-auctions-calendar',
        standalone: true,
imports: [
    FormsModule,
    BreadCrumbsComponent,
    TimelineViewComponent,
    TranslateModule
],
    templateUrl: './auctions-calendar.component.html',
    styleUrl: './auctions-calendar.component.scss'
})
export class AuctionsCalendarComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];
  currentLang: any;
  isBrowser: boolean;

  // Filter state
  selectedFilter: string = 'Upcoming';
  filterOptions: string[] = ['Upcoming', '2024', '2025', '2026'];
  searchTerm: string = '';

  // Auction data
  auctions: AuctionEvent[] = [];
  filteredAuctions: AuctionEvent[] = [];

  // Loading and error states
  isLoading: boolean = false;
  errorMessage: string = '';
  hasError: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    public localeService: LocaleService,
    private ngxTranslate: NgxTranslateService,
    private auctionsApiService: AuctionsCalendarApiService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.loadAuctions();
    }
  }

  loadAuctions() {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.auctionsApiService.getAuctionsCalendar().subscribe({
      next: (auctions) => {
        this.auctions = auctions;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        this.hasError = true;
        this.errorMessage =
          error.message || 'Failed to load auctions. Please try again.';
        this.isLoading = false;
        console.error('Error loading auctions:', error);
      },
    });
  }

  retryLoadAuctions() {
    this.loadAuctions();
  }

  onFilterChange() {
    this.applyFilters();
  }

  applyFilters() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered: AuctionEvent[] = [];

    // Filter by selected option (Upcoming or Year)
    if (this.selectedFilter === 'Upcoming') {
      // Show only future auctions within 365 days from today
      const oneYearFromToday = new Date(today);
      oneYearFromToday.setFullYear(today.getFullYear() + 1);

      filtered = [...this.auctions].filter(
        (auction) => auction.date >= today && auction.date <= oneYearFromToday
      );
    } else {
      // Filter by specific year
      const year = parseInt(this.selectedFilter);
      filtered = [...this.auctions].filter(
        (auction) => auction.date.getFullYear() === year
      );
    }

    // Filter by search term (search in current language)
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter((auction) => {
        const title = getTranslation(auction.title, this.localeService.languageCode);
        const location = getTranslation(auction.location, this.localeService.languageCode);
        const description = getTranslation(
          auction.description,
          this.localeService.languageCode
        );

        return (
          title.toLowerCase().includes(term) ||
          location.toLowerCase().includes(term) ||
          description.toLowerCase().includes(term)
        );
      });
    }

    this.filteredAuctions = filtered;
  }

  getTranslatedFilter(filter: string): string {
    if (filter === 'Upcoming') {
      return this.ngxTranslate.instant('auctions_calendar.filters.upcoming');
    }
    // For year filters, return as is (numbers don't need translation)
    return filter;
  }
}
